import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import ReAddHoldingDialog from "@/components/benchmark/ReAddHoldingDialog";
import { DIST_PORTFOLIOS, distLabel } from "./distConfig";

interface CbamFund { id: string; fe_fund_name: string; fund_manager: string | null; fund_name: string; fund_class: string | null; isin: string | null; is_current: boolean; }
interface FundWeight { id: string; fund_id: string; portfolio: number; weight_pct: number; effective_date: string; }

export default function DistFundManagement() {
  const queryClient = useQueryClient();
  const [showAddFund, setShowAddFund] = useState(false);
  const [newFund, setNewFund] = useState({ fund_name: "", fe_fund_name: "", fund_manager: "", isin: "" });

  const { data: funds } = useQuery({
    queryKey: ["ba-cbam-funds-all", "v2-with-class-isin"],
    queryFn: async () => {
      const { data, error } = await supabase.from("ba_cbam_funds").select("id, fe_fund_name, fund_manager, fund_name, fund_class, isin, is_current").order("fund_name");
      if (error) throw error;
      return data as CbamFund[];
    },
    staleTime: 0,
  });

  const { data: weights } = useQuery({
    queryKey: ["ba-dist-fund-weights-all"],
    queryFn: async () => {
      const { data, error } = await supabase.from("ba_dist_cbam_fund_weights").select("*").order("portfolio");
      if (error) throw error;
      return data as FundWeight[];
    },
  });

  const addFundMutation = useMutation({
    mutationFn: async (fund: typeof newFund) => {
      if (!fund.isin.trim()) throw new Error("ISIN is required");
      const { error } = await supabase.from("ba_cbam_funds").insert({
        fund_name: fund.fund_name, fe_fund_name: fund.fe_fund_name, fund_manager: fund.fund_manager || null, isin: fund.isin.trim(),
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ba-cbam-funds-all"] });
      setShowAddFund(false);
      setNewFund({ fund_name: "", fe_fund_name: "", fund_manager: "", isin: "" });
      toast.success("Fund added");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const updateWeightMutation = useMutation({
    mutationFn: async ({ fund_id, portfolio, weight_pct }: { fund_id: string; portfolio: number; weight_pct: number }) => {
      const { error } = await supabase.from("ba_dist_cbam_fund_weights").upsert(
        { fund_id, portfolio, weight_pct, effective_date: new Date().toISOString().split("T")[0] },
        { onConflict: "fund_id,portfolio,effective_date" }
      );
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["ba-dist-fund-weights-all"] }),
  });

  const removeFundMutation = useMutation({
    mutationFn: async (fundId: string) => {
      // Exit the fund by dating it out, never by deleting. ba_dist_cbam_fund_weights is
      // effective-dated and every historical reconstruction reads "the latest row per fund on
      // or before date X" (src/lib/blendedPortfolioMatrix.ts), so a DELETE erases the fund's
      // whole Dist history. Writing weight_pct = 0 at today's date is the schema's exit
      // convention: the latest weight becomes 0, so the fund drops out of this table (which
      // filters on latest weight > 0) and trg_sync_is_current clears is_current unless the
      // fund is still held in another range.
      const today = new Date().toISOString().split("T")[0];
      const { error: wErr } = await supabase.from("ba_dist_cbam_fund_weights").upsert(
        DIST_PORTFOLIOS.map((portfolio) => ({ fund_id: fundId, portfolio, weight_pct: 0, effective_date: today })),
        { onConflict: "fund_id,portfolio,effective_date" }
      );
      if (wErr) throw wErr;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ba-cbam-funds-all"] });
      queryClient.invalidateQueries({ queryKey: ["ba-dist-fund-weights-all"] });
      toast.success("Fund removed from Dist range");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Latest-effective weight per (fund_id, portfolio) — an exit is an explicit weight_pct = 0
  // row at the exit date, so the newest row is the only one that describes today's holding.
  const getWeight = (fundId: string, portfolio: number) => {
    const rows = (weights || []).filter((w) => w.fund_id === fundId && w.portfolio === portfolio);
    if (rows.length === 0) return 0;
    return Number(rows.reduce((a, b) => (a.effective_date > b.effective_date ? a : b)).weight_pct) || 0;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Dist Fund Holdings and Weightings</h1>
          <p className="text-sm text-muted-foreground">Manage CBAM fund holdings, weights, and sector mappings (Dist range)</p>
        </div>
        <div className="flex items-center gap-2">
        <ReAddHoldingDialog
          weightsTable="ba_dist_cbam_fund_weights"
          portfolios={DIST_PORTFOLIOS.map((n) => ({ num: n, label: distLabel(n) }))}
          rangeLabel="Dist"
          invalidateKeys={[["ba-cbam-funds-all"], ["ba-dist-fund-weights-all"]]}
        />
        <Dialog open={showAddFund} onOpenChange={setShowAddFund}>
          <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-1" /> Add Fund</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add New Fund</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Display Name</Label><Input value={newFund.fund_name} onChange={(e) => setNewFund({ ...newFund, fund_name: e.target.value })} /></div>
              <div><Label>FE Fund Name (exact match)</Label><Input value={newFund.fe_fund_name} onChange={(e) => setNewFund({ ...newFund, fe_fund_name: e.target.value })} /></div>
              <div><Label>Fund Manager</Label><Input value={newFund.fund_manager} onChange={(e) => setNewFund({ ...newFund, fund_manager: e.target.value })} /></div>
              <div><Label>ISIN <span className="text-destructive">*</span></Label><Input value={newFund.isin} onChange={(e) => setNewFund({ ...newFund, isin: e.target.value.toUpperCase() })} maxLength={12} /></div>
              <p className="text-xs text-muted-foreground">
                For a fund that was previously held and sold out, use "Re-add Holding" instead —
                adding it again here fails on the unique FE fund name.
              </p>
              <Button onClick={() => addFundMutation.mutate(newFund)} disabled={!newFund.fund_name || !newFund.fe_fund_name || !newFund.isin.trim()}>Add Fund</Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-lg">Fund Holdings & Weights</CardTitle></CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="text-left p-3 font-medium text-muted-foreground min-w-[100px]">Manager</th>
                <th className="text-left p-3 font-medium text-muted-foreground min-w-[200px]">Fund</th>
                <th className="text-left p-3 font-medium text-muted-foreground min-w-[100px]">Fund Class</th>
                <th className="text-left p-3 font-medium text-muted-foreground min-w-[120px]">ISIN</th>
                {DIST_PORTFOLIOS.map((p) => (
                  <th key={p} className="text-center p-3 font-medium text-muted-foreground min-w-[80px]">{distLabel(p)} %</th>
                ))}
                <th className="text-center p-3 font-medium text-muted-foreground min-w-[60px]">Active</th>
                <th className="text-center p-3 font-medium text-muted-foreground min-w-[60px]">Remove</th>
              </tr>
            </thead>
            <tbody>
              {funds?.filter((fund) => DIST_PORTFOLIOS.some((p) => getWeight(fund.id, p) > 0)).slice().sort((a, b) => {
                const m = (a.fund_manager || "").localeCompare(b.fund_manager || "", undefined, { sensitivity: "base" });
                if (m !== 0) return m;
                const f = (a.fund_name || "").localeCompare(b.fund_name || "", undefined, { sensitivity: "base" });
                if (f !== 0) return f;
                return (a.fund_class || "").localeCompare(b.fund_class || "", undefined, { sensitivity: "base" });
              }).map((fund, idx) => (
                <tr key={fund.id} className={cn("border-b", idx % 2 === 0 ? "bg-background" : "bg-muted/20")}>
                  <td className="p-3 text-xs text-muted-foreground">{fund.fund_manager || "—"}</td>
                  <td className="p-3 text-xs font-medium">{fund.fund_name}</td>
                  <td className="p-3 text-xs text-muted-foreground">{fund.fund_class || "—"}</td>
                  <td className="p-3 text-xs font-mono text-muted-foreground">{fund.isin || <span className="text-destructive">—</span>}</td>
                  {DIST_PORTFOLIOS.map((p) => (
                    <td key={p} className="p-2 text-center">
                      <Input
                        key={`${fund.id}-${p}-${getWeight(fund.id, p)}`}
                        type="number" step="0.01"
                        className="w-20 h-7 text-xs text-center mx-auto"
                        defaultValue={Number(getWeight(fund.id, p)).toFixed(2)}
                        onBlur={(e) => {
                          const val = parseFloat(e.target.value);
                          if (!isNaN(val)) updateWeightMutation.mutate({ fund_id: fund.id, portfolio: p, weight_pct: val });
                        }}
                      />
                    </td>
                  ))}
                  <td className="p-2 text-center">
                    <Badge variant={fund.is_current ? "default" : "secondary"} className="text-[10px]">{fund.is_current ? "Active" : "Legacy"}</Badge>
                  </td>
                  <td className="p-2 text-center">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remove {fund.fund_name} from Dist range?</AlertDialogTitle>
                          <AlertDialogDescription>This sets all Dist weights to 0% as of today, so the fund drops out of the Dist range. Its weight history is kept.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => removeFundMutation.mutate(fund.id)}>Remove</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
